<!-- Las tres lentes y el conteo. Las lentes son botones con estado (`aria-pressed`), no pestañas:
     la pestaña es la vista de arriba. -->
<div class="pt-toolbar">
  <button type="button" class="pt-lente" [class.is-on]="lens === 'open'" [attr.aria-pressed]="lens === 'open'" (click)="setLens('open')">
    <i class="fa-solid fa-hourglass-half" aria-hidden="true"></i>
    <span>Abiertas</span>
  </button>
  <button type="button" class="pt-lente" [class.is-on]="lens === 'ready'" [attr.aria-pressed]="lens === 'ready'" (click)="setLens('ready')">
    <i class="fa-solid fa-flag-checkered" aria-hidden="true"></i>
    <span>Listas para cerrar</span>
    <span class="pt-lente__n" *ngIf="readyCount !== null">{{ readyCount }}</span>
  </button>
  <button
    type="button"
    class="pt-lente"
    [class.is-on]="lens === 'closed'"
    [attr.aria-pressed]="lens === 'closed'"
    (click)="setLens('closed')"
  >
    <i class="fa-solid fa-box-archive" aria-hidden="true"></i>
    <span>Cerradas</span>
  </button>

  <!-- Unificar: para el historial que el saneamiento dejó como sesiones de uno aunque se vieron el mismo día. -->
  <button
    *ngIf="showSelection"
    pButton
    type="button"
    icon="fa-solid fa-object-group"
    class="p-button-sm p-button-outlined sl-merge"
    [label]="'Unificar (' + selected.size + ')'"
    [disabled]="selected.size < 2 || merging"
    [loading]="merging"
    pTooltip="Junta en una sola sesión las que se vieron juntas: mueve sus arrendamientos, une votantes y actas. Se conserva la más antigua"
    tooltipPosition="bottom"
    (click)="confirmMerge()"
  ></button>
  <span class="pt-count" *ngIf="!loading && !error">
    <b>{{ visibleRows.length }}</b> {{ visibleRows.length === 1 ? 'sesión' : 'sesiones' }}
  </span>
</div>

<div class="pt-wrap">
  <table class="pt-table">
    <thead>
      <tr>
        <th class="pt-col-sel" *ngIf="showSelection">
          <p-checkbox
            [binary]="true"
            [ngModel]="allSelected"
            (onChange)="toggleAll()"
            [disabled]="!selectable.length"
            ariaLabel="Marcar todas las sesiones abiertas de votación"
          ></p-checkbox>
        </th>
        <th>Fecha</th>
        <th>Tipo</th>
        <th>Arrendamientos</th>
        <th>Votantes</th>
        <th class="ta-c">Actas</th>
        <th>Estado</th>
        <th>Resultado</th>
      </tr>
    </thead>

    <!-- El esqueleto tiene la geometría final: al llegar los datos nada salta de sitio. -->
    <tbody *ngIf="loading">
      <tr *ngFor="let s of skeleton" aria-hidden="true">
        <td *ngIf="showSelection"></td>
        <td><p-skeleton height="0.8rem" width="6rem"></p-skeleton></td>
        <td><p-skeleton height="1.3rem" width="5rem" borderRadius="6px"></p-skeleton></td>
        <td><p-skeleton height="0.8rem" width="9rem"></p-skeleton></td>
        <td><p-skeleton height="0.8rem" width="7rem"></p-skeleton></td>
        <td><p-skeleton height="0.8rem" width="2rem"></p-skeleton></td>
        <td><p-skeleton height="1.3rem" width="4.5rem" borderRadius="6px"></p-skeleton></td>
        <td><p-skeleton height="0.8rem" width="6rem"></p-skeleton></td>
      </tr>
    </tbody>

    <tbody *ngIf="!loading">
      <!-- La fila entera es el enlace: se abre con clic o con Enter, y el foco se ve al tabular. -->
      <tr
        *ngFor="let r of visibleRows"
        class="pt-row is-link"
        tabindex="0"
        [attr.aria-label]="'Abrir la sesión del ' + (r.sessionDate | date: 'dd/MM/yyyy')"
        (click)="open(r)"
        (keydown.enter)="open(r)"
      >
        <!-- La celda de selección no navega: el clic se queda en la casilla. -->
        <td class="pt-col-sel" *ngIf="showSelection" (click)="$event.stopPropagation()" (keydown.enter)="$event.stopPropagation()">
          <p-checkbox
            *ngIf="r.status === 'open' && r.kind === 'voting'"
            [binary]="true"
            [ngModel]="isSelected(r)"
            (onChange)="toggle(r)"
            [ariaLabel]="'Marcar la sesión del ' + (r.sessionDate | date: 'dd/MM/yyyy')"
          ></p-checkbox>
        </td>
        <td class="pt-fecha">{{ r.sessionDate | date: 'dd/MM/yyyy HH:mm' }}</td>
        <td>
          <span class="pt-pill" [class.is-info]="r.kind === 'voting'" [class.is-muted]="r.kind !== 'voting'">{{ kindLabel[r.kind] }}</span>
        </td>
        <td>
          <b class="pt-num">{{ r.itemsCount }}</b>
          <span class="sl-folios" *ngIf="r.generalComments">{{ r.generalComments }}</span>
        </td>
        <td>
          <ng-container *ngIf="r.kind === 'voting'; else sinVotantes">
            <span class="pt-pill pt-num" [class.is-ok]="r.readyToClose" [class.is-warn]="!r.readyToClose"
              >{{ votersDone(r) }}/{{ r.voters.length }} votaron</span
            >
            <span class="sl-faltan" *ngIf="missingVoters(r) as faltan">faltan: {{ faltan }}</span>
          </ng-container>
          <ng-template #sinVotantes><span class="pt-dash">—</span></ng-template>
        </td>
        <td class="ta-c">
          <ng-container *ngIf="r.minutesCount; else sinActas">
            <i class="fa-regular fa-file-lines sl-acta" aria-hidden="true"></i> <b class="pt-num">{{ r.minutesCount }}</b>
          </ng-container>
          <!-- Cerrada y sin acta es trabajo pendiente y se ve como tal; abierta sin acta es lo normal. -->
          <ng-template #sinActas>
            <span class="pt-pill is-warn" *ngIf="r.status === 'closed'; else guion">Sin acta</span>
            <ng-template #guion><span class="pt-dash">—</span></ng-template>
          </ng-template>
        </td>
        <td>
          <span class="pt-pill" [ngClass]="'is-' + (statusPill[r.status]?.tone || 'muted')">{{
            statusPill[r.status]?.label || r.status
          }}</span>
          <span class="pt-pill is-ok sl-lista" *ngIf="r.status === 'open' && r.readyToClose && r.kind === 'voting'">Lista para cerrar</span>
        </td>
        <td>
          <!-- Tres conteos con forma además de color: ✓ aprobados · ✗ rechazados · ~ en revisión. -->
          <span class="sl-res">
            <span class="sl-res-ok" pTooltip="Aprobados" tooltipPosition="top"
              ><i class="fa-solid fa-check" aria-hidden="true"></i>{{ r.results.approved }}</span
            >
            <span class="sl-res-bad" pTooltip="Rechazados" tooltipPosition="top"
              ><i class="fa-solid fa-xmark" aria-hidden="true"></i>{{ r.results.rejected }}</span
            >
            <span class="sl-res-warn" pTooltip="En revisión" tooltipPosition="top"
              ><i class="fa-solid fa-rotate" aria-hidden="true"></i>{{ r.results.review }}</span
            >
            <span class="sl-res-open" *ngIf="r.results.open" pTooltip="Sin decidir todavía" tooltipPosition="top">
              <i class="fa-regular fa-clock" aria-hidden="true"></i>{{ r.results.open }}
            </span>
          </span>
        </td>
      </tr>

      <tr *ngIf="!visibleRows.length">
        <td [attr.colspan]="showSelection ? 8 : 7" class="pt-vacio">
          <ng-container *ngIf="error; else sinNada">{{ error }}</ng-container>
          <ng-template #sinNada>
            <ng-container [ngSwitch]="lens">
              <ng-container *ngSwitchCase="'open'"
                >No hay sesiones abiertas. Las nuevas se crean desde «Nueva sesión» o desde Por originar.</ng-container
              >
              <ng-container *ngSwitchCase="'ready'">Ninguna sesión abierta tiene todos los votos todavía.</ng-container>
              <ng-container *ngSwitchCase="'closed'">Todavía no hay sesiones cerradas.</ng-container>
            </ng-container>
          </ng-template>
        </td>
      </tr>
    </tbody>
  </table>
</div>

<p-confirmDialog key="unificar" appendTo="body" styleClass="sd-confirm" [style]="{ width: '520px' }"></p-confirmDialog>
